<script>
import { mapState, mapActions } from 'vuex';
import { isEmpty, sortBy } from 'lodash';

export default {
  props: ['value'],
  data() {
    return {
      settings: {
        operatorSkills: [],
        operatorChannels: [],
        operatorGroups: [],
      },
      sending: false,
      MIN_GROUP_ACTIVITIES_COUNT: 0,
      MAX_GROUP_ACTIVITIES_COUNT: 100,
      requestTypePriorities: [],
      skillsInChannels: [],
      labels: [],
      loading: true,
      ctsUserId: '',
      useExpress: false,
      ctsUserIdErr: false,
      avatarURL: null,
    };
  },
  async created() {
    await this.loadSkills({ onlyActive: true });
    await this.getUser(this.value.id)
      .then((res) => {
        if (res.operatorTeam) {
          this.requestTypePriorities = res.operatorTeam.requestTypePriorities;
        }
        const { ctsUserId, useExpress, avatarUrl } = res;
        this.ctsUserId = ctsUserId;
        this.useExpress = useExpress;
        this.avatarURL = avatarUrl;
      });
    await this.getOperatorSettings({ operatorId: this.value.id })
      .then((res) => {
        const operatorSkills = res.skills
          .filter((skill) => this.operatorTeamSkills.find((item) => item === skill.requestType.id))
          .map((skill) => ({
            requestTypePriorityId: skill.id,
            id: skill.requestType.id,
            title: skill.requestType.name,
            value: skill.weight,
            type: 'slider',
          }));
        this.settings.operatorSkills = sortBy(operatorSkills, (skill) => skill.title.toLowerCase());
        const operatorChannels = res.channels
          .filter((channel) => channel.allTeamsOn
              || channel.teams.map((team) => team && team.id).find((item) => item && item.toString() === (this.operatorTeamId && this.operatorTeamId.toString())))
          .map((channel) => ({
            id: channel.id,
            name: channel.name,
            value: this.operatorHasChannel(channel, res.userChannels),
            type: 'boolean',
          }));
        this.settings.operatorChannels = sortBy(
          operatorChannels,
          (channel) => channel.name.toLowerCase(),
        );
        this.settings.operatorGroups = res.groupUsers
          .filter((groupUser) => {
            const channelsIdInGroup = res.channelsGroups
              .filter((group) => group.id === groupUser.group.id)
              .map((group) => group.channels)[0].map((channel) => channel.id);
            const operatorChannelsId = this.settings.operatorChannels.map((item) => item.id);

            return operatorChannelsId
              .some((operatorChannelId) => channelsIdInGroup.includes(operatorChannelId));
          })
          .map((groupUser) => ({
            id: Number(groupUser.id),
            title: groupUser.group.name,
            value: groupUser.activitiesLimit,
            type: 'integer',
          }));
      })
      .then(() => {
        if (this.unavailableTeamChannelsOrSkills) {
          this.loading = false;
        }
        return Promise.resolve();
      });
    if (!this.unavailableTeamChannelsOrSkills) {
      const setLabels = this.setLabels();
      const setSkillsInChannels = this.setSkillsInChannels();
      Promise.all([setLabels, setSkillsInChannels])
        .then(() => {
          this.loading = false;
          return Promise.resolve();
        });
    }
  },
  watch: {
    ctsUserId() {
      this.ctsUserIdErr = false;
    },
  },
  computed: {
    ...mapState('overseer', ['maximumSkillValue']),
    ...mapState('skillsList', ['skills']),
    ...mapState('home', ['user', 'currentTeam', 'ucceIntegration']),
    operatorTeamId() {
      return this.user.role === 'overseer' ? this.currentTeam : this.value.operatorTeamId;
    },
    operatorTeamSkills() {
      const skills = this.skills
        .filter((item) => {
          const teams = item.teams
            .filter((team) => {
              if (this.operatorTeamId) {
                return team.id.toString() === this.operatorTeamId.toString();
              } return false;
            });
          return teams.length !== 0;
        });
      return skills.map((skill) => skill.id);
    },
    unavailableTeamChannelsOrSkills() {
      return isEmpty(this.settings.operatorChannels) || isEmpty(this.settings.operatorSkills);
    },
    table() {
      return {
        key: 'id',
        cols: this.labels,
        rows: this.skillsInChannels,
      };
    },
    showData() {
      return !this.unavailableTeamChannelsOrSkills && !this.loading;
    },
    operatorGroupsSettings() {
      const operatorGroupsRank = {
        Чат: 1,
        Почта: 2,
        'Социальные сети': 3,
      };
      return sortBy(this.settings.operatorGroups, (group) => operatorGroupsRank[group.title]);
    },
    ctsIdRules() {
      if (this.ctsUserIdErr || (isEmpty(this.ctsUserId) && this.useExpress)) {
        return [this.$t('T_CTS_USER_ID_ERROR')];
      }
      return [true];
    },
  },
  methods: {
    ...mapActions('overseer', ['getOperatorSettings', 'setOperatorSettings']),
    ...mapActions('skillsList', ['loadSkills']),
    ...mapActions('home', ['getUser']),
    async save() {
      const operatorId = this.value.id;
      const updateSkills = !this.unavailableTeamChannelsOrSkills
        ? this.settings.operatorSkills.map((skill) => ({
          id: skill.requestTypePriorityId,
          weight: this.setSkillWeightById(this.labels, skill.id),
        })) : [];
      const updateChannelsInput = !this.unavailableTeamChannelsOrSkills
        ? this.settings.operatorChannels.map((channel) => ({
          channelId: channel.id,
          turnOn: this.setChannelValueById(this.skillsInChannels, channel.id),
        })) : [];
      const updateActivitiesLimitInput = !this.unavailableTeamChannelsOrSkills
        ? this.settings.operatorGroups.map((group) => ({
          groupUserId: group.id,
          activitiesLimit: Number(group.value),
        })) : [];
      this.sending = true;
      const updateOperatorExpressInput = {
        ctsUserId: this.ctsUserId,
        useExpress: this.useExpress,
      };
      this.setOperatorSettings({
        operatorId,
        updateSkills,
        updateChannelsInput,
        updateActivitiesLimitInput,
        updateOperatorExpressInput,
      }).then(() => {
        this.sending = false;
        this.close();
      }).catch((err) => {
        this.sending = false;
        if (err.graphQLErrors[0].error.payload[0].field === 'cts_user_id') this.ctsUserIdErr = true;
      });
    },
    close() {
      this.$parent.close([null]);
    },
    setChannelValueById(data, id) {
      return data.find((item) => item.id === id).value;
    },
    setSkillWeightById(data, id) {
      const { weight } = data.filter((item) => item.weight !== undefined)
        .find((item) => {
          const nameNum = item.name.startsWith('skillId')
            ? item.name.replace('skillId', '') : item.name;
          return nameNum === id;
        });
      const res = weight !== null ? weight : 0;
      return res !== null ? res : 0;
    },
    operatorHasChannel(channel, userChannels) {
      const index = userChannels.findIndex((userChannel) => userChannel.channel.id === channel.id);
      return index !== -1;
    },
    setLabels() {
      this.labels = this.settings.operatorSkills.map((skill) => ({
        name: `skillId${skill.id}`, // без `skillId` - ошибки для навыков с именем в виде числа
        // (при обращении к ключам объекта)
        label: skill.title,
        weight: skill.value,
        classHeader: this.$style.columnWidth,
      }));
      const firstColElem = {
        name: 'id',
        label: this.$t('channel_skill'),
        classHeader: this.$style.channelsColumn,
      };
      this.labels.unshift(firstColElem);
    },
    setSkillsInChannels() {
      // парсим данные о связке каналов и навыков с бэка для таблицы
      this.skillsInChannels = this.settings.operatorChannels.map((channel) => {
        const row = {
          id: channel.id,
          value: channel.value,
        };
        const skillsInChannelsIds = this.requestTypePriorities
          .filter((priority) => priority.channel.id === channel.id)
          .map((item) => item.requestType)
          .map((requestType) => requestType.id);

        for (let i = 0; i < this.settings.operatorSkills.length; i += 1) {
          const keyByTeamSkillId = this.settings.operatorSkills[i].id;
          const skillIdKey = `skillId${keyByTeamSkillId}`;
          if (skillsInChannelsIds.includes(keyByTeamSkillId)) {
            row[skillIdKey] = true;
          } else {
            row[skillIdKey] = false;
          }
        }
        return row;
      });
    },
    showChannels(id) {
      return this.settings.operatorChannels.filter((channel) => channel.id === id)
        .map((channel) => channel.name).toString();
    },
    isEmpty(value) {
      return isEmpty(value);
    },
  },
};
</script>

<template lang="pug">
div(:class="$style.root")
  header(:class="$style.header")
    ui-user-badge(
    :title="value.title",
    :src="value.avatarUrl || avatarURL"
    :subtitle="value.email",
    type="operator")
  div(:class="$style.attention"
    v-if="ucceIntegration && showData")
    | {{ $t('T_ATTENTION_UCCE_INTEGRATION_SETTINGS') }}
  div(:class="$style.subHeader" v-if="showData") {{ $t('T_EXPRESS_INTEGRATION') }}
  div(:class="$style.expressIntegration" v-if="showData")
    v-text-field(:label="$t('T_EXPRESS_ID')", v-model="ctsUserId", :required="useExpress" :rules="ctsIdRules")
    ui-checkbox(v-model.sync="useExpress" colored="darkGreen") {{ $t('T_IS_ACTIV') }}
  div(:class="$style.subHeader" v-if="showData") {{ $t('T_CHANNEL_LIMITS') }}
  div(:class="$style.limits" v-if="showData")
    div(v-for="skill in operatorGroupsSettings", :class="[$style.skill, !skill.value && $style.skillDisabled]")
      div(:class="$style.skillLabel") {{ skill.title }}
      div(:class="$style.skillLevel")
        ui-input-number(:value="skill.value", :min="MIN_GROUP_ACTIVITIES_COUNT",
          :max="MAX_GROUP_ACTIVITIES_COUNT", v-model="skill.value",
          placeholder="Max количество активностей", v-if="skill.type === 'integer'")
  div(:class="$style.subHeader" v-if="showData") {{ $t('T_OPERATOR_CHANNELS_AND_SKILLS') }}
  div(:class="[$style.tableWrapper, loading && $style.tableWrapperMargin]")
    ui-progress(:class="$style.loading" v-if="loading")
    ui-table(
      v-if="showData",
      :data="table",
      :class="$style.table")
      template(v-for="label in labels" :slot="label.label" slot-scope="{ data }")
        ui-input-number(v-if="label.weight !== undefined", :value="label.weight",
          :min="0", :max="maximumSkillValue", v-model="label.weight", :class="$style.input", data-test="INPUT_DATE_NUMBER")
        div(v-else :class="$style.skillLevelHeader") {{ $t('T_COMPETENCE') }}
      template(v-for="label in labels" :slot="label.name", slot-scope="{ data }")
        div(v-if="label.name ==='id'"
          :class="[!data.value && $style.disabled, $style.channelName]") {{ showChannels(data.id) }}
          ui-checkbox(v-model="data.value", colored="main", :class="$style.checkbox")
        ui-checkbox(v-model="data[label.name]", v-if="data && label.name !=='id'", colored="darkGreen", disabled)
  div(v-if="unavailableTeamChannelsOrSkills && !loading"
    :class="$style.subHeader") {{ $t('T_NO_AVAILABLE_CHANNELS_OR_SKILLS') }}
  div(:class="$style.notification" v-if="showData") {{ $t('T_MODAL_CHANGE_SKILLS_WITHIN_CHANNELS_HINT') }}
  footer(:class="$style.footer")
    ui-button(@click.native="close()", @keyup.enter.native="close()") {{ $t('T_CANCEL') }}
    ui-button(colored="transpPrimary", @click.native="!sending ? save() : ''",
      @keyup.enter.native="!sending ? save() : ''",
      :disabled="sending || (useExpress && isEmpty(ctsUserId) || unavailableTeamChannelsOrSkills)", data-test="BUTTON_SAVE_CONFIG") {{ $t('T_SAVE') }}
</template>

<style lang="sass" module>
.root
  display: block
  max-height: 800px
  max-width: 1100px
.header
  padding: 24px 40px
  border-bottom: 1px solid $color_divider
.footer
  padding: 24px 40px
  text-align: right
  margin-bottom: 20px
.limits
  padding: 0 40px 20px
  min-width: 532px
.subHeader
  padding: 24px 40px
  font-size: 18px
.tableWrapper
  position: relative
.loading
  position: absolute
  left: 0
  right: 0
  bottom: 0
.disabled
  color: $color_text_second
.skill
  display: flex
  align-items: center
  line-height: 18px
  transition: color $a_speed
  & + &
    margin-top: 16px
  &Disabled
    color: $color_text_second
  &Label
    width: 188px
    overflow: hidden
    text-overflow: ellipsis
    font-size: 14px
    margin-right: 24px
    vertical-align: baseline
    &.disabled
      color: $color_text_second
  &Count
    margin-left: 8px
    font-size: 12px
    color: $color_text_second
    vertical-align: baseline
    white-space: nowrap
    width: 64px
  &Level
    width: 248px
    display: flex
    flex-direction: column
    align-items: stretch
    &Header
      margin-top: 21px
    .channels
      margin: 6px 0
.table
  padding: 0 24px
.notification
  white-space: pre-wrap
  margin: 20px 40px 10px
  color: $color_main_second
.columnWidth
  min-width: 100px
  max-width: 100px
  word-break: break-word
  white-space: pre-wrap
.channelName
  display: inline-flex
  justify-content: space-between
  width: 100%
.channelsColumn
  max-width: 200px
  display: inline-flex
  justify-content: space-between
.checkbox
  max-width: 20px
  margin-left: 30px
  margin-right: 40px
.input
  margin: 10px 0
  max-width: 55px

@media (-ms-high-contrast: active) and (-ms-high-contrast: none)
  .columnWidth
    word-break: break-all

@-moz-document url-prefix()
  .input
    position: absolute
    bottom: 0
.attention
  display: flex
  align-items: center
  padding: 16px
  min-width: 375px
  border-radius: 8px
  border: 1px solid $color_red
  background-color: rgba(235, 88, 88, 0.1)
  color: #e41e1e
  margin: 24px 24px 0
.expressIntegration
  padding: 0 40px 20px
  min-width: 532px

</style>
